/**
 * NatalPage — страница натальной карты
 *
 * Загружает реальные данные через API и отображает их
 * в динамическом SVG-компоненте NatalChartDynamic.
 *
 * API endpoints:
 *   GET  /calculations/natal-chart/:userId  — получить карту
 *   POST /calculations/natal-chart           — рассчитать карту
 *   POST /calculations/natal-chart/interpret  — AI-интерпретация
 *   GET  /export/natal-chart/pdf              — экспорт PDF
 *   GET  /export/natal-chart/json             — экспорт JSON
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionGuard from '../components/SubscriptionGuard';
import AccessBadge from '../components/access/AccessBadge';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { SubscriptionTier } from '../../shared/types/access';
import api, {
  fetchNatalChart,
  calculateNatalChart,
  interpretNatalChart,
  NatalChartResponse,
  NatalChartPlanet,
  NatalChartHouse,
  NatalChartAspect,
} from '../api';
import { api as apiDirect } from '../api';
import { renderMarkdown } from '../utils/renderMarkdown';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import ErrorState from '../components/ErrorState';
import NatalChartDynamic from '../components/NatalChartDynamic';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, PAGE_SEO } from '../utils/seo';
import NatalChartSVG from '../components/NatalChartSVG';
import { RUSSIA_REGIONS } from '../../shared/data/russiaRegions';

// ============================================================
// Адаптер: NatalChartResponse -> форматы для SVG-компонентов
// ============================================================

/** Адаптирует ответ API в формат NatalChartDynamic */
function adaptForDynamic(chart: NatalChartResponse): {
  planets: NatalChartPlanet[];
  houses: NatalChartHouse[];
  aspects: NatalChartAspect[];
  ascendantLongitude: number;
} {
  return {
    planets: chart.planets,
    houses: chart.houses,
    aspects: chart.aspects,
    ascendantLongitude: chart.ascendantLongitude,
  };
}

/** Адаптирует ответ API в формат NatalChartSVG */
function adaptForSVG(chart: NatalChartResponse): {
  planets: Array<{
    name: string;
    nameRu: string;
    symbol: string;
    sign: string;
    signRu: string;
    longitude: number;
    house: number;
    retrograde: boolean;
  }>;
  houses: Array<{
    number: number;
    sign: string;
    signRu: string;
    longitude: number;
  }>;
  aspects: Array<{
    planet1: string;
    planet2: string;
    type: string;
    orb: number;
  }>;
  ascendantLongitude: number;
} {
  return {
    planets: chart.planets.map((p) => ({
      name: p.name,
      nameRu: p.nameRu,
      symbol: p.symbol,
      sign: p.sign,
      signRu: p.signRu,
      longitude: p.longitude,
      house: p.house,
      retrograde: p.retrograde,
    })),
    houses: chart.houses.map((h) => ({
      number: h.number,
      sign: h.sign,
      signRu: h.signRu,
      longitude: h.longitude,
    })),
    aspects: chart.aspects.map((a) => ({
      planet1: a.planet1,
      planet2: a.planet2,
      type: a.type,
      orb: a.orb,
    })),
    ascendantLongitude: chart.ascendantLongitude,
  };
}

// ============================================================
// Таблицы аспектов
// ============================================================

const ASPECT_LABELS: Record<string, string> = {
  conjunction: 'Соединение',
  opposition: 'Оппозиция',
  trine: 'Трин',
  square: 'Квадрат',
  sextile: 'Секстиль',
  quincunx: 'Квинконс',
  'semi-sextile': 'Полусекстиль',
  'semi-square': 'Полуквадрат',
  sesquiquadrate: 'Сесквиквадрат',
  quintile: 'Квинтиль',
  'bi-quintile': 'Биквинтиль',
  'Соединение': 'Соединение',
  'Оппозиция': 'Оппозиция',
  'Трин': 'Трин',
  'Квадрат': 'Квадрат',
  'Секстиль': 'Секстиль',
  'Квинконс': 'Квинконс',
};

// ============================================================
// Стили
// ============================================================

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  padding: '24px',
  marginBottom: '20px',
  position: 'relative',
  overflow: 'hidden',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(28px, 5vw, 40px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '24px',
  textAlign: 'center',
  animation: 'goldShimmer 3s ease-in-out infinite',
};

const buttonPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '10px 24px',
  borderRadius: '25px',
  color: '#0a0814',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  transition: 'all 0.3s',
  boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
};

const buttonOutlineStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(212, 175, 55, 0.4)',
  color: '#d4af37',
  fontSize: '14px',
  padding: '8px 20px',
  borderRadius: '20px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  transition: 'all 0.3s',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  background: 'rgba(10, 8, 20, 0.6)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  borderRadius: '10px',
  color: '#e0e0e0',
  fontSize: '14px',
  fontFamily: 'var(--font-sans)',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#d4af37',
  fontSize: '13px',
  marginBottom: '6px',
  fontFamily: 'var(--font-sans)',
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  color: '#d4af37',
  fontSize: '13px',
  fontFamily: 'var(--font-sans)',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  color: '#e0e0e0',
};

// ============================================================
// Декорации карточки
// ============================================================

const CardDecorations: React.FC = () => (
  <>
    <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
    <div style={{ position: 'absolute', inset: 0, borderRadius: '24px', background: 'linear-gradient(135deg, rgba(212,175,55,0.15), transparent 50%)', pointerEvents: 'none' }} />
  </>
);

// ============================================================
// Компонент
// ============================================================

const NatalPage: React.FC = () => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { hasAccess, userTier, requiredTier, isLoading: accessLoading } = useSubscriptionAccess('natal-chart');
  const [chart, setChart] = useState<NatalChartResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [interpretLoading, setInterpretLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);

  // SEO
  useEffect(() => {
    setMetaTags({
      ...PAGE_SEO.natal,
      canonicalUrl: buildCanonicalUrl('/natal'),
    });
    return () => { resetMetaTags(); };
  }, []);

  // Переключатель: динамический или старый SVG
  const [useDynamic, setUseDynamic] = useState(true);

  // Форма расчёта
  const [showForm, setShowForm] = useState(false);
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');

  /** Приблизительное смещение часовых поясов России от UTC */
  const TIMEZONE_OFFSETS: Record<string, number> = {
    'Europe/Kaliningrad': 2, 'Europe/Moscow': 3, 'Europe/Samara': 4,
    'Asia/Yekaterinburg': 5, 'Asia/Omsk': 6, 'Asia/Novosibirsk': 7,
    'Asia/Irkutsk': 8, 'Asia/Yakutsk': 9, 'Asia/Vladivostok': 10,
    'Asia/Magadan': 11, 'Asia/Kamchatka': 12,
  };

  /** Поиск координат по названию города в справочнике регионов */
  const geocodeCity = (cityName: string) => {
    const name = cityName.trim().toLowerCase();
    if (!name) return null;
    for (const fd of RUSSIA_REGIONS) {
      for (const subject of fd.subjects) {
        for (const district of subject.districts) {
          for (const settlement of district.settlements) {
            if (settlement.name.toLowerCase() === name) {
              return {
                latitude: settlement.latitude,
                longitude: settlement.longitude,
                timezone: settlement.timezone,
                timezoneOffset: TIMEZONE_OFFSETS[settlement.timezone] ?? 3,
              };
            }
          }
        }
      }
    }
    return null;
  };

  // Загрузка натальной карты
  const loadChart = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNatalChart(user.id);
      if (res.success && res.data) {
        setChart(res.data);
        setShowForm(false);
      } else {
        setShowForm(true);
        setChart(null);
      }
    } catch {
      setError('Не удалось загрузить натальную карту');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  // POST — расчёт карты
  const handleCalculate = async () => {
    if (!user?.id) return;
    setError(null);
    setLoading(true);
    try {
      const coords = geocodeCity(birthPlace);
      if (!coords) {
        setError('Город не найден в справочнике. Поддерживаются только города России. Укажите ближайший крупный город.');
        setLoading(false);
        return;
      }
      const res = await calculateNatalChart({
        birthDate,
        birthTime: birthTime || undefined,
        birthPlace: {
          city: birthPlace,
          country: 'Россия',
          latitude: coords.latitude,
          longitude: coords.longitude,
          timezone: coords.timezone,
          timezoneOffset: coords.timezoneOffset,
        },
      });
      if (res.success && res.data) {
        setChart(res.data);
        setShowForm(false);
      } else {
        setError(res.error || res.message || 'Ошибка расчёта');
      }
    } catch {
      setError('Ошибка при расчёте натальной карты');
    } finally {
      setLoading(false);
    }
  };

  // AI-интерпретация
  const handleInterpret = async () => {
    if (!user?.id || !chart) return;
    setInterpretLoading(true);
    setInterpretation(null);
    try {
      const res = await interpretNatalChart({
        chart,
        profile: {
          full_name: user.id,
          birth_date: birthDate || new Date().toISOString().split('T')[0],
          birth_time: birthTime || undefined,
          birth_place: birthPlace || 'Россия',
        },
      });
      if (res.success && res.data?.interpretation) {
        setInterpretation(res.data.interpretation);
      } else {
        setError(res.error || 'Не удалось получить интерпретацию');
      }
    } catch {
      setError('Ошибка при запросе интерпретации');
    } finally {
      setInterpretLoading(false);
    }
  };

  // Загрузка сохранённого AI-описания при загрузке карты
  useEffect(() => {
    if (!user?.id) return;
    const fetchSavedDescription = async () => {
      try {
        const res = await apiDirect.get<string>('/ai-content/natal-description');
        if (res.success && res.data) {
          setInterpretation(res.data);
        }
      } catch {
        // Не критично — пользователь может сгенерировать вручную
      }
    };
    if (chart) fetchSavedDescription();
  }, [user?.id, chart]);

  // Экспорт PDF
  const handleExportPDF = async () => {
    if (!user?.id) return;
    setExportLoading('pdf');
    try {
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${baseUrl}/api/export/natal-chart/pdf`, {
        credentials: 'include',
      });
      if (res.ok) {
        const blob = await res.blob();
        downloadBlob(blob, 'natal-chart.pdf');
      }
    } catch {
      setError('Ошибка экспорта в PDF');
    } finally {
      setExportLoading(null);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // Render
  // ============================================================

  if (loading) return <AnimatedLoadingState />;

  const adapted = chart ? adaptForDynamic(chart) : null;
  const adaptedSvg = chart ? adaptForSVG(chart) : null;

  return (
    <SubscriptionGuard requireTier="free" featureId="natal-chart">
      <style>{`
        @keyframes goldShimmer {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 200% center; }
        }
      `}</style>
      <div style={{
        minHeight: '80vh',
        padding: isMobile ? '20px 16px' : '32px 20px',
        maxWidth: '1000px',
        margin: '0 auto',
      }}>
        {/* Заголовок */}
        <h1 style={titleStyle}>Натальная карта</h1>

        {/* AccessBadge — показывает статус доступа */}
        {!accessLoading && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <AccessBadge
              featureId="natal-chart"
              userTier={userTier as SubscriptionTier}
              requiredTier={requiredTier as SubscriptionTier}
              featureName="Натальная карта"
              size="sm"
            />
          </div>
        )}

      {error && <ErrorState message={error} onRetry={loadChart} />}

      {/* Форма расчёта */}
      {showForm && (
        <div style={cardStyle}>
          <CardDecorations />
          <h2 style={{
            color: '#f0f0f0',
            fontSize: isMobile ? '16px' : '20px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '20px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}>
            Рассчитать натальную карту
          </h2>
          <div style={{ display: 'grid', gap: '16px', maxWidth: '500px', margin: '0 auto' }}>
            <div>
              <label style={labelStyle}>Дата рождения *</label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Время рождения</label>
              <input
                type="time"
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Место рождения *</label>
              <input
                type="text"
                placeholder="Город, страна"
                value={birthPlace}
                onChange={(e) => setBirthPlace(e.target.value)}
                style={inputStyle}
              />
            </div>
            <button
              onClick={handleCalculate}
              disabled={!birthDate || !birthPlace}
              style={{
                ...buttonPrimaryStyle,
                opacity: !birthDate || !birthPlace ? 0.5 : 1,
                cursor: !birthDate || !birthPlace ? 'not-allowed' : 'pointer',
                alignSelf: 'center',
              }}
              onMouseEnter={(e) => { if (birthDate && birthPlace) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.4)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)'; }}
            >
              Рассчитать
            </button>
          </div>
        </div>
      )}

      {/* Карта рассчитана */}
      {chart && (
        <>
          {/* SVG — динамический или старый */}
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <CardDecorations />
            {/* Переключатель */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setUseDynamic(true)}
                style={{
                  ...buttonOutlineStyle,
                  background: useDynamic ? 'rgba(212, 175, 55, 0.15)' : 'none',
                  borderColor: useDynamic ? '#d4af37' : 'rgba(212, 175, 55, 0.3)',
                }}
              >
                Динамическая
              </button>
              <button
                onClick={() => setUseDynamic(false)}
                style={{
                  ...buttonOutlineStyle,
                  background: !useDynamic ? 'rgba(212, 175, 55, 0.15)' : 'none',
                  borderColor: !useDynamic ? '#d4af37' : 'rgba(212, 175, 55, 0.3)',
                }}
              >
                Классическая
              </button>
            </div>

            {useDynamic && adapted ? (
              <NatalChartDynamic
                planets={adapted.planets}
                houses={adapted.houses}
                aspects={adapted.aspects}
                ascendantLongitude={adapted.ascendantLongitude}
                size={500}
              />
            ) : adaptedSvg ? (
              <NatalChartSVG
                planets={adaptedSvg.planets}
                houses={adaptedSvg.houses}
                aspects={adaptedSvg.aspects}
                ascendantLongitude={adaptedSvg.ascendantLongitude}
                size={500}
              />
            ) : null}
          </div>

          {/* Кнопки действий */}
          <div style={{
            ...cardStyle,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'center',
          }}>
            <CardDecorations />
            <button
              onClick={handleInterpret}
              disabled={interpretLoading}
              style={{
                ...buttonPrimaryStyle,
                opacity: interpretLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!interpretLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.4)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)'; }}
            >
              {interpretLoading ? 'Генерация...' : 'Описание'}
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exportLoading === 'pdf'}
              style={buttonOutlineStyle}
              onMouseEnter={(e) => { if (exportLoading !== 'pdf') { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {exportLoading === 'pdf' ? 'Загрузка...' : 'Экспорт PDF'}
            </button>
          </div>

          {/* AI-интерпретация */}
          {interpretation && (
            <div style={cardStyle}>
              <CardDecorations />
              <h3 style={{
                color: '#f0f0f0',
                fontSize: isMobile ? '14.4px' : '18px',
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                marginBottom: '12px',
                position: 'relative',
                zIndex: 1,
              }}>
                Интерпретация
              </h3>
              <div
                style={{
                  color: '#e0e0e0',
                  fontSize: '15px',
                  lineHeight: 1.8,
                  fontFamily: 'var(--font-sans)',
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(interpretation)) }}
              />
            </div>
          )}

          {/* Таблица планет */}
          <div style={cardStyle}>
            <CardDecorations />
            <h3 style={{
              color: '#f0f0f0',
              fontSize: isMobile ? '14.4px' : '18px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              marginBottom: '16px',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}>
              Планеты
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
                fontFamily: 'var(--font-sans)',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
                    <th style={thStyle}>Планета</th>
                    <th style={thStyle}>Знак</th>
                    <th style={thStyle}>Градус</th>
                    <th style={thStyle}>Дом</th>
                    <th style={thStyle}>R</th>
                  </tr>
                </thead>
                <tbody>
                  {chart.planets.map((p) => (
                    <tr key={p.name} style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.08)' }}>
                      <td style={tdStyle}>{p.symbol} {p.nameRu}</td>
                      <td style={tdStyle}>{p.signRu}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {p.longitude.toFixed(1)}°
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{p.house}</td>
                      <td style={{
                        ...tdStyle,
                        textAlign: 'center',
                        color: p.retrograde ? '#ff6b6b' : '#888',
                      }}>
                        {p.retrograde ? 'R' : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Таблица аспектов */}
          <div style={cardStyle}>
            <CardDecorations />
            <h3 style={{
              color: '#f0f0f0',
              fontSize: isMobile ? '14.4px' : '18px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              marginBottom: '16px',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}>
              Аспекты
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
                fontFamily: 'var(--font-sans)',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
                    <th style={thStyle}>Планета 1</th>
                    <th style={thStyle}>Аспект</th>
                    <th style={thStyle}>Планета 2</th>
                    <th style={thStyle}>Орбис</th>
                  </tr>
                </thead>
                <tbody>
                  {chart.aspects.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.08)' }}>
                      <td style={tdStyle}>
                        {chart.planets.find((p) => p.name === a.planet1)?.nameRu || a.planet1}
                      </td>
                      <td style={tdStyle}>{ASPECT_LABELS[a.type] || a.type}</td>
                      <td style={tdStyle}>
                        {chart.planets.find((p) => p.name === a.planet2)?.nameRu || a.planet2}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {a.orb.toFixed(1)}°
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Нет карты и нет формы */}
      {!loading && !chart && !showForm && !error && (
        <div style={cardStyle}>
          <CardDecorations />
          <p style={{ color: '#b0b0b0', textAlign: 'center', fontSize: isMobile ? '12px' : '15px', position: 'relative', zIndex: 1 }}>
            Нажмите кнопку ниже, чтобы рассчитать натальную карту.
          </p>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={() => setShowForm(true)}
              style={buttonPrimaryStyle}
            >
              Рассчитать карту
            </button>
          </div>
        </div>
      )}
    </div>
    </SubscriptionGuard>
  );
};

export default NatalPage;
